import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Heart, ShoppingBag, Menu, X, User, LogOut, Package } from "lucide-react";
import { useToggle } from "../contexts/ToggleContext";
import { useSiteSettings } from "../contexts/SiteSettingsContext";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { IfEnabled } from "./IfEnabled";
import { api } from "../lib/api";

const NAV_SLUGS = [
    "sarees",
    "kurtis-kurta-sets",
    "lehengas",
    "anarkali",
    "gowns",
    "indo-western",
    "blouses",
];

// Kept as a constant so designers can later wire this to SiteSettings.
const FEATURED_CATEGORY_SLUGS = ["sarees", "blouses"];

export function Header() {
    const navigate = useNavigate();
    const { settings } = useSiteSettings();
    const { user, logout } = useCustomerAuth();
    const [navCats, setNavCats] = useState([]);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [accountOpen, setAccountOpen] = useState(false);
    const [query, setQuery] = useState("");
    const accountRef = useRef(null);

    const tagline = (settings?.tagline || "").trim();
    const taglineOn = useToggle("tagline");
    const searchOn = useToggle("search");
    const customerAuthOn = useToggle("customer_auth");

    useEffect(() => {
        api.get("/categories")
            .then(({ data }) => {
                const map = new Map(data.map((c) => [c.slug, c]));
                const ordered = NAV_SLUGS.map((s) => map.get(s)).filter(Boolean);
                setNavCats(ordered);
            })
            .catch(() => setNavCats([]));
    }, []);

    useEffect(() => {
        const onClick = (e) => {
            if (accountRef.current && !accountRef.current.contains(e.target)) setAccountOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const submitSearch = (e) => {
        e.preventDefault();
        if (!query.trim()) return;
        navigate(`/search?q=${encodeURIComponent(query.trim())}`);
        setSearchOpen(false);
        setMobileOpen(false);
    };

    const handleLogout = async () => {
        await logout();
        setAccountOpen(false);
        navigate("/");
    };

    return (
        <header className="bg-ivory border-b border-inkDark/5 sticky top-0 z-40 backdrop-blur supports-[backdrop-filter]:bg-ivory/85" data-testid="site-header">
            <div className="max-w-[1400px] mx-auto px-5 lg:px-12">
                <div className="flex items-center justify-between py-4 lg:py-5">
                    <button
                        className="lg:hidden p-2 -ml-2 text-inkDark"
                        aria-label="Open menu"
                        onClick={() => setMobileOpen((v) => !v)}
                        data-testid="mobile-menu-button"
                    >
                        {mobileOpen ? <X size={22} /> : <Menu size={22} />}
                    </button>

                    <Link to="/" className="flex flex-col items-center lg:items-start mx-auto lg:mx-0" data-testid="brand-logo">
                        <span className="font-display text-2xl sm:text-3xl tracking-tight text-inkDark leading-none">
                            Sukaanyas
                        </span>
                        {taglineOn && tagline ? (
                            <span className="mt-1 label-eyebrow text-[10px] sm:text-[11px]" data-testid="brand-tagline">
                                {tagline}
                            </span>
                        ) : null}
                    </Link>

                    <div className="flex items-center gap-1 sm:gap-3">
                        <IfEnabled flag="search">
                            <button
                                className="p-2 text-inkDark hover:text-skyblueDeep transition-colors"
                                onClick={() => setSearchOpen((v) => !v)}
                                aria-label="Search"
                                data-testid="search-icon-button"
                            >
                                <Search size={20} />
                            </button>
                        </IfEnabled>

                        <IfEnabled flag="wishlist">
                            <Link to="/wishlist" className="p-2 text-inkDark hover:text-skyblueDeep transition-colors hidden sm:block" aria-label="Wishlist" data-testid="wishlist-icon-button">
                                <Heart size={20} />
                            </Link>
                        </IfEnabled>

                        <IfEnabled flag="cart">
                            <Link to="/cart" className="p-2 text-inkDark hover:text-skyblueDeep transition-colors" aria-label="Cart" data-testid="cart-icon-button">
                                <ShoppingBag size={20} />
                            </Link>
                        </IfEnabled>

                        {customerAuthOn ? (
                            user ? (
                                <div className="relative" ref={accountRef}>
                                    <button
                                        onClick={() => setAccountOpen((v) => !v)}
                                        className="flex items-center gap-2 px-2 py-1 rounded-full hover:bg-beige transition-colors"
                                        data-testid="account-menu-button"
                                    >
                                        {user.picture ? (
                                            <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
                                        ) : (
                                            <span className="w-7 h-7 rounded-full bg-skyblue text-inkDark inline-flex items-center justify-center text-xs font-medium">
                                                {(user.name || user.email)[0]?.toUpperCase()}
                                            </span>
                                        )}
                                        <span className="hidden sm:inline text-sm text-inkDark max-w-[8rem] truncate">{user.name || user.email.split("@")[0]}</span>
                                    </button>
                                    {accountOpen ? (
                                        <div className="absolute right-0 mt-2 w-56 bg-ivory rounded-2xl shadow-lift border border-inkDark/5 py-2" data-testid="account-dropdown">
                                            <div className="px-4 py-2 text-xs text-inkMuted truncate">{user.email}</div>
                                            <Link to="/orders" onClick={() => setAccountOpen(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-inkDark hover:bg-beige" data-testid="account-orders-link">
                                                <Package size={14} /> My orders
                                            </Link>
                                            <Link to="/account/loyalty" onClick={() => setAccountOpen(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-inkDark hover:bg-beige" data-testid="account-loyalty-link">
                                                <User size={14} /> My Sukaanyas
                                            </Link>
                                            <Link to="/wishlist" onClick={() => setAccountOpen(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-inkDark hover:bg-beige">
                                                <Heart size={14} /> Wishlist
                                            </Link>
                                            <button onClick={handleLogout} className="w-full text-left flex items-center gap-2 px-4 py-2 text-sm text-inkDark hover:bg-beige" data-testid="account-logout-button">
                                                <LogOut size={14} /> Sign out
                                            </button>
                                        </div>
                                    ) : null}
                                </div>
                            ) : (
                                <Link to="/login" className="hidden sm:inline-flex items-center gap-1 text-sm text-inkDark hover:text-skyblueDeep px-3 py-1.5 rounded-full border border-inkDark/10" data-testid="header-signin-link">
                                    <User size={14} /> Sign in
                                </Link>
                            )
                        ) : null}
                    </div>
                </div>

                <nav className="hidden lg:flex items-center justify-center gap-7 pb-4" data-testid="desktop-nav">
                    {navCats.map((c) => {
                        const featured = FEATURED_CATEGORY_SLUGS.includes(c.slug);
                        return (
                            <Link
                                key={c.slug}
                                to={`/category/${c.slug}`}
                                className={
                                    featured
                                        ? "label-eyebrow px-3 py-1.5 rounded-full bg-skyblue/25 text-skyblueDeep hover:bg-skyblue/40 transition-colors"
                                        : "label-eyebrow hover:text-skyblueDeep transition-colors"
                                }
                                data-testid={`nav-link-${c.slug}`}
                                data-featured={featured ? "true" : undefined}
                            >
                                {c.name}
                            </Link>
                        );
                    })}
                    <Link to="/category/accessories" className="label-eyebrow hover:text-skyblueDeep transition-colors" data-testid="nav-link-accessories">
                        Accessories
                    </Link>
                </nav>

                {searchOn && searchOpen ? (
                    <form
                        onSubmit={submitSearch}
                        className="pb-4 flex items-center gap-3 border-t border-inkDark/5 pt-4"
                        data-testid="search-form"
                    >
                        <Search size={18} className="text-inkMuted" />
                        <input
                            autoFocus
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search sarees, kurtis, lehengas..."
                            className="w-full bg-transparent outline-none text-inkDark placeholder:text-inkMuted/70 font-sans"
                            data-testid="search-input"
                        />
                        <button type="submit" className="btn-pill text-sm" data-testid="search-submit-button">
                            Search
                        </button>
                    </form>
                ) : null}

                {mobileOpen ? (
                    <nav className="lg:hidden pb-5 pt-2 border-t border-inkDark/5 flex flex-col gap-3 animate-fade-up" data-testid="mobile-nav">
                        {navCats.map((c) => {
                            const featured = FEATURED_CATEGORY_SLUGS.includes(c.slug);
                            return (
                                <Link
                                    key={c.slug}
                                    to={`/category/${c.slug}`}
                                    onClick={() => setMobileOpen(false)}
                                    className={
                                        featured
                                            ? "label-eyebrow text-skyblueDeep bg-skyblue/25 px-3 py-2 rounded-full w-fit"
                                            : "label-eyebrow text-inkDark"
                                    }
                                    data-testid={`mobile-nav-link-${c.slug}`}
                                    data-featured={featured ? "true" : undefined}
                                >
                                    {c.name}
                                </Link>
                            );
                        })}
                        <Link to="/category/accessories" onClick={() => setMobileOpen(false)} className="label-eyebrow text-inkDark">
                            Accessories
                        </Link>
                        {customerAuthOn && !user ? (
                            <Link to="/login" onClick={() => setMobileOpen(false)} className="label-eyebrow text-skyblueDeep">Sign in</Link>
                        ) : null}
                    </nav>
                ) : null}
            </div>
        </header>
    );
}
